/**
 * Foreground messaging handler for FCM.
 * Should be called once at app startup.
 */
import { getMessagingInstance } from "../config.js";
import { onMessage } from "firebase/messaging";
const VALID_ORIGIN = window?.location?.origin ?? "";
function isValidAppUrl(url) {
    if (!VALID_ORIGIN)
        return false;
    try {
        const parsed = new URL(url, VALID_ORIGIN);
        if (parsed.origin !== VALID_ORIGIN)
            return false;
        // Allow only known app paths
        const allowedPaths = [
            "/", "/book", "/history", "/messages",
            "/profile", "/invoices", "/tickets", "/notifications",
            "/booking/", "/invoice/", "/driver/",
            "/admin/", "/business/",
        ];
        return allowedPaths.some((p) => parsed.pathname.startsWith(p));
    }
    catch {
        return false;
    }
}
/**
 * Play an alarm sound using Web Audio API.
 * This is a fallback for web/PWA where push notification sounds
 * are not supported by browsers. Only works when app is in foreground.
 */
function playAlarmSound() {
    try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        oscillator.type = "square";
        // Alternating frequencies to mimic an alarm siren
        oscillator.frequency.setValueAtTime(800, audioCtx.currentTime);
        oscillator.frequency.setValueAtTime(1000, audioCtx.currentTime + 0.2);
        oscillator.frequency.setValueAtTime(800, audioCtx.currentTime + 0.4);
        oscillator.frequency.setValueAtTime(1000, audioCtx.currentTime + 0.6);
        oscillator.frequency.setValueAtTime(800, audioCtx.currentTime + 0.8);
        oscillator.frequency.setValueAtTime(1000, audioCtx.currentTime + 1.0);
        gainNode.gain.setValueAtTime(0.5, audioCtx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 1.2);
        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        oscillator.start();
        oscillator.stop(audioCtx.currentTime + 1.2);
    }
    catch (e) {
        console.warn("[FCM] Web Audio alarm failed:", e);
    }
}
function isUrgentType(type) {
    if (!type)
        return false;
    return /new_booking|new_job|dispatch_alert|emergency|driver_stranded|urgent/.test(type);
}
export function initForegroundMessaging(fallbackRoute = "/") {
    const messaging = getMessagingInstance();
    if (!messaging)
        return;
    onMessage(messaging, (payload) => {
        console.log("[FCM] Foreground message received:", payload);
        const data = payload.data || {};
        const title = payload.notification?.title || "ItemGo";
        const body = payload.notification?.body || "";
        const link = data.link || fallbackRoute;
        const type = data.type || "";
        // Play alarm sound for urgent notifications in foreground
        if (isUrgentType(type)) {
            playAlarmSound();
        }
        // Show a native notification in foreground with click handler
        const hasNotificationSupport = typeof window !== "undefined" && "Notification" in window;
        if (hasNotificationSupport && Notification.permission === "granted") {
            const notif = new Notification(title, {
                body,
                icon: "/notification-icon-192x192.png",
                badge: "/notification-badge-96x96.png",
                data,
            });
            notif.onclick = () => {
                notif.close();
                if (isValidAppUrl(link)) {
                    window.focus();
                    window.location.href = link;
                }
            };
        }
        // Trigger toast via custom event
        window.dispatchEvent(new CustomEvent("itemgo:notification", {
            detail: { title, body, link },
        }));
        // Auto-navigate if link is valid and different
        if (typeof window !== "undefined" && isValidAppUrl(link)) {
            const currentPath = window.location.pathname;
            const targetPath = new URL(link, window.location.origin).pathname;
            if (currentPath !== targetPath) {
                setTimeout(() => {
                    window.location.href = link;
                }, 1500);
            }
        }
    });
}
//# sourceMappingURL=foregroundMessaging.js.map